import React, { useState, useEffect, useMemo } from 'react';
import HeaderElements from '../navigation/components/HeaderElements';
import UIButtonToolbar from '../navigation/components/UIButtonToolbar';
import './Recommendations.scss';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import Loader from '../common/Loader/Loader';
import { fetchColumnsData } from '../../dispatchers/ColumnSelectorDispatcher';
import RGL, { WidthProvider, Layout } from 'react-grid-layout';
import { filterWidgetItemsForRecommendationScreen } from '../../well-chart/WidgetLayoutService';
import OpportunitiesChart from './Opportunities/OpportunitiesChart';
import {
  fetchDepthChangeData,
  fetchESPPumpChangeData,
  fetchFrequencyChangeData,
  fetchInjectionRateIncreaseData,
  fetchInjectionRateReductionData,
  fetchOpportunitiesData,
  fetchTrackedChangesFRQ,
  fetchTrackedChangesIPI,
  fetchWHPReductionData,
} from '../../services/recommendations/RecommendationsService';
import RecommendationsTableWidget from './Table-widgets/RecommendationTableWidget';
import {
  depthChangeTableColumns,
  frequencyRateTableColumns,
  injectionRateReductionTableColumns,
  injectRateIncreaseTableColumns,
  pumpChangeTableColumns,
  trackedFrequencyRateTableColumns,
  trackedInjectionGasRateTableColumns,
  whpReductionTableColumns,
} from './Table-widgets/TableData/RecommendationsTableData';
import SelectedWellFilters from '../well-filters/SelectedWellFilters';
import { handleRemoveFilter } from '../well-filters/wellFilterUtils';
import { useLocation } from 'react-router-dom';
import MenuNavIcon from '../navigation/components/NavMenuIcon';

const GridLayout = WidthProvider(RGL);

export interface TabContent {
  id: number;
  name: string;
  active: boolean;
}

const Recommendations: React.FC = () => {
  const dispatch = useAppDispatch();
  const recommendationsPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const chartActiveTab = useAppSelector((state: any) => state.Recommendations?.chartActiveTab);
  const recommendationsPageDataLoading = useAppSelector((state: any) => state.testWellColumnsSlice.loading);
  const selectedFilters = useAppSelector((state) => state.WellFiltersSlice.PCSelectedFilters);
  const pcWellIds = useAppSelector((state) => state.WellFiltersSlice.PCWellIds);

  const frequencyChangeData = useAppSelector((state: any) => state.Recommendations?.frequencyChange?.data);
  const trackedFrequencyRateData = useAppSelector((state: any) => state.Recommendations?.trackedChangesFRQ?.data);
  const pumpChangeData = useAppSelector((state: any) => state.Recommendations?.espPumpChange?.data);
  const injectRateIncreaseData = useAppSelector((state: any) => state.Recommendations?.injectionRateIncrease?.data);
  const trackedInjectionRateData = useAppSelector((state: any) => state.Recommendations?.trackedChangesIPI?.data);
  const injectionRateReductionData = useAppSelector(
    (state: any) => state.Recommendations?.injectionRateReduction?.data,
  );
  const whpReductionData = useAppSelector((state: any) => state.Recommendations?.whpReduction?.data);
  const depthChangeData = useAppSelector((state: any) => state.Recommendations?.depthChange?.data);
  const location = useLocation();
  const locationState = location.state || {};
  const tab = locationState.tab;
  const [activeTab, setActiveTab] = useState<string>(tab && ['esp', 'gl'].includes(tab) ? tab : 'esp');
  const [scrollY, setScrollY] = useState(0);

  const filterParams = useMemo(() => {
    const getFilterIds = (section: string) =>
      selectedFilters
        .filter((f: any) => f.section === section && typeof f.id === 'number')
        .map((f: any) => f.id)
        .join(',');

    const areaIds = getFilterIds('Area');
    const businessUnitIds = getFilterIds('Business unit');
    const fieldIds = getFilterIds('Field');

    const params: any = {};
    if (areaIds !== '') params.areaIds = areaIds;
    if (businessUnitIds !== '') params.businessUnitIds = businessUnitIds;
    if (fieldIds !== '') params.fieldIds = fieldIds;
    if (pcWellIds && pcWellIds.length > 0) {
      params.wellIds = pcWellIds;
    }

    return params;
  }, [selectedFilters, pcWellIds]);

  useEffect(() => {
    dispatch(fetchColumnsData(`${activeTab}-recommendations`));
  }, [dispatch, activeTab]);

  useEffect(() => {
    const fetchTableData = async () => {
      if (activeTab === 'esp') {
        await Promise.all([
          dispatch(fetchFrequencyChangeData(filterParams)),
          dispatch(fetchTrackedChangesFRQ(filterParams)),
          dispatch(fetchESPPumpChangeData(filterParams)),
        ]);
      } else if (activeTab === 'gl') {
        await Promise.all([
          dispatch(fetchInjectionRateIncreaseData(filterParams)),
          dispatch(fetchTrackedChangesIPI(filterParams)),
          dispatch(fetchInjectionRateReductionData(filterParams)),
          dispatch(fetchWHPReductionData(filterParams)),
          dispatch(fetchDepthChangeData(filterParams)),
        ]);
      }
    };
    fetchTableData();
  }, [dispatch, activeTab, filterParams]);

  useEffect(() => {
    const fetchChartData = async () => {
      // Opportunities chart
      let mopValue = 'ESP';
      if (activeTab === 'esp') {
        mopValue = chartActiveTab === 'PwfHz' ? 'PwfHz' : 'ESP';
      } else if (activeTab === 'gl') {
        mopValue = 'GL';
      }
      await dispatch(fetchOpportunitiesData({ MOP: mopValue, ...filterParams }));
    };
    fetchChartData();
  }, [dispatch, activeTab, chartActiveTab, filterParams]);

  // Make tabButtons reactive to activeTab changes
  const tabButtons: TabContent[] = useMemo(
    () => [
      {
        id: 0,
        name: 'ESP recommendations',
        active: activeTab === 'esp',
      },
      {
        id: 1,
        name: 'GL recommendations',
        active: activeTab === 'gl',
      },
    ],
    [activeTab],
  );

  useEffect(() => {
    // Handle scroll events to track vertical scroll position
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setScrollY(scrollTop);
    };
    window.addEventListener('scroll', handleScroll);

    // Cleanup: Remove scroll event listener when component unmounts
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const widgetParam = (locationState.widget as string) || '';
    if (
      [
        'glRecommendationsInjectionRate',
        'glRecommendationsDepthChange',
        'espRecommendationsPumpChange',
        'espRecommendationsFrequencyChange',
      ].includes(widgetParam) &&
      !recommendationsPageDataLoading
    ) {
      setTimeout(() => {
        const element = document.getElementById('grid-item-' + widgetParam);
        if (element) {
          const navBar = document.querySelector('.recommendation-nav-bar') as HTMLElement;
          const navBarHeight = navBar ? navBar.offsetHeight : 0;
          const extraOffset = 25; // 20-30px extra offset
          const y = element.getBoundingClientRect().top + window.pageYOffset - navBarHeight - extraOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 2000);
    }
  }, [locationState, recommendationsPageDataLoading]);

  const handleTabChange = (tabId: number) => {
    const tab = tabButtons.find((button) => button.id === tabId);
    if (tab) {
      if (tab.name === 'ESP recommendations') {
        setActiveTab('esp');
      } else if (tab.name === 'GL recommendations') {
        setActiveTab('gl');
      }
    }
  };

  const safeLayout = (recommendationsPageData: any[]): Layout[] => {
    recommendationsPageData = filterWidgetItemsForRecommendationScreen(recommendationsPageData, activeTab);
    return recommendationsPageData
      ?.map((obj: any, index: number): Layout | null => {
        const lg = obj?.layout?.lg;
        if (!lg || !obj?.key) {
          console.warn(`Invalid layout at index ${index}`, obj);
          return null;
        }

        const item: Layout = {
          i: String(obj.key),
          x: lg.x,
          y: lg.y,
          w: lg.width,
          h: lg.height,
          minH: lg.minH ?? 1,
          minW: lg.minW ?? 6,
        };

        return item;
      })
      .filter((item): item is Layout => item !== null);
  };

  const layoutMemo = useMemo(() => safeLayout(recommendationsPageData), [recommendationsPageData, activeTab]);

  // determine if there are selected filters so SCSS can add spacing if needed
  const hasSelectedFilters = Array.isArray(selectedFilters) && selectedFilters.length > 0;

  const handleRemoveFilterUtil = (filterToRemove: any) => {
    handleRemoveFilter(filterToRemove, selectedFilters, dispatch);
  };

  return (
    <div className={`summary-dashboard ${hasSelectedFilters ? 'has-selected-filters' : ''}`}>
      <div className={`summary-header-container ${scrollY > 0 ? 'fix-nav-bar' : ''}`}>
        <div className='group-status-top-row'>
          <div className='nav-menu-icon-header-container'>
            <MenuNavIcon />
            <span className='group-status-group-name'>Recommendations</span>
          </div>
          <div className='custom-views-header-elements'>
            <HeaderElements showWellsTab={true} showFilterIcon={true} />
          </div>
        </div>
      </div>
      <div className='summary-header-filters-section'>
        <SelectedWellFilters
          filters={selectedFilters}
          onRemoveFilter={handleRemoveFilterUtil}
          recommendationsPage={true}
        />
      </div>
      <div className='asset-ui-buttons-container recommendations-ui-buttons'>
        <UIButtonToolbar uiButtons={tabButtons} buttonClicked={handleTabChange} />
      </div>
      <div className='recommendations-ui-container flex h-full flex-column'>
        {recommendationsPageDataLoading ? (
          <Loader />
        ) : (
          <div className='recommendations-component-container'>
            <GridLayout
              layout={layoutMemo}
              cols={12}
              rowHeight={30}
              isDraggable={false}
              isResizable={true}
              className='recommendations-layout-react-grid'
              useCSSTransforms={false}
              isDroppable={false}
            >
              {layoutMemo?.map((item: any) => (
                <div key={item.i} id={`grid-item-${item.i}`} className='grid-item'>
                  {item.i === 'espRecommendationsCharts' && (
                    <OpportunitiesChart header='Recommendations' parentActiveTab={activeTab} fullScreen={true} />
                  )}
                  {item.i === 'espRecommendationsFrequencyChange' && (
                    <RecommendationsTableWidget
                      widgetHeader='Frequency change'
                      columnData={frequencyRateTableColumns()}
                      tableData={frequencyChangeData ?? []}
                    />
                  )}
                  {item.i === 'espRecommendationsTrackedFrequency' && (
                    <RecommendationsTableWidget
                      widgetHeader='Tracked frequency changes'
                      columnData={trackedFrequencyRateTableColumns()}
                      statusLegend={true}
                      tableData={trackedFrequencyRateData ?? []}
                    />
                  )}
                  {item.i === 'espRecommendationsPumpChange' && (
                    <RecommendationsTableWidget
                      widgetHeader='Pump change'
                      columnData={pumpChangeTableColumns()}
                      tableData={pumpChangeData ?? []}
                    />
                  )}
                  {item.i === 'espRecommendationsNotFeasible' && (
                    <RecommendationsTableWidget
                      widgetHeader='Not feasible recommendations'
                      columnData={frequencyRateTableColumns()}
                      tableData={[]}
                    />
                  )}
                  {item.i === 'glRecommendationsCharts' && (
                    <OpportunitiesChart header='Recommendations' parentActiveTab={activeTab} fullScreen={true} />
                  )}
                  {item.i === 'glRecommendationsInjectionRate' && (
                    <RecommendationsTableWidget
                      widgetHeader='Injection rate increase'
                      columnData={injectRateIncreaseTableColumns()}
                      tableData={injectRateIncreaseData ?? []}
                    />
                  )}
                  {item.i === 'glRecommendationsTrackedInjectedGasRate' && (
                    <RecommendationsTableWidget
                      widgetHeader='Tracked injected gas rate changes'
                      columnData={trackedInjectionGasRateTableColumns()}
                      statusLegend={true}
                      tableData={trackedInjectionRateData ?? []}
                    />
                  )}
                  {item.i === 'glRecommendationsInjectionRateReduction' && (
                    <RecommendationsTableWidget
                      widgetHeader='Injection rate reduction'
                      columnData={injectionRateReductionTableColumns()}
                      tableData={injectionRateReductionData ?? []}
                    />
                  )}
                  {item.i === 'glRecommendationsWHPReduction' && (
                    <RecommendationsTableWidget
                      widgetHeader='WHP reduction'
                      columnData={whpReductionTableColumns()}
                      tableData={whpReductionData ?? []}
                    />
                  )}
                  {item.i === 'glRecommendationsDepthChange' && (
                    <RecommendationsTableWidget
                      widgetHeader='Depth change'
                      columnData={depthChangeTableColumns()}
                      tableData={depthChangeData ?? []}
                    />
                  )}
                  {item.i === 'glRecommendationsNotFeasible' && (
                    <RecommendationsTableWidget
                      widgetHeader='Not feasible'
                      columnData={injectRateIncreaseTableColumns()}
                      tableData={[]}
                    />
                  )}
                </div>
              ))}
            </GridLayout>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recommendations;
